import {Button} from 'primereact/button';
import {Checkbox} from 'primereact/checkbox';
import {InputText} from 'primereact/inputtext';
import {ProgressBar} from 'primereact/progressbar';
import {SelectButton} from 'primereact/selectbutton';
import React, {useEffect, useState} from 'react';
import {Field, useForm} from '@tanstack/react-form';
import {useMutation} from '@tanstack/react-query';
import {DatePicker} from '../../components/date-picker/date-picker';
import {useAuth} from '../../providers/auth/auth-provider';
import {useMessage} from '../../providers/message/message-provider';
import {fetch} from '../../shared/api/v1';
import type {components} from '../../shared/api/v1/openapi.types';
import {
  digitsOnly,
  emailFormat,
  isValidOptionalDigits,
  isValidOptionalText,
  isValidRequiredDigits,
  isValidRequiredEmail,
  isValidRequiredText,
  maxDigits,
  maxLength,
  minLength,
  required,
} from '../../utils/validation';

type CreatePartnerDto = components['schemas']['CreatePartnerDto'];
type UpdatePartnerDto = components['schemas']['UpdatePartnerDto'];
type PartnerEntity = components['schemas']['Partner'];

type FormValues = CreatePartnerDto;

const ORG_TYPE_OPTIONS = [
  { label: 'Юр. лицо / ИП', value: 0 },
  { label: 'Физ. лицо', value: 1 },
];

const RequiredMark = () => <span className="p-error"> *</span>;

const RequiredLabel = ({ children }: { children: React.ReactNode }) => (
  <span>
    {children}
    <RequiredMark />
  </span>
);


const isOrgType0Valid = (values: FormValues) =>
  isValidRequiredText(values.name, 3, 99) &&
  isValidOptionalText(values.shortName, 30) &&
  isValidRequiredText(values.director, undefined, 99) &&
  isValidRequiredDigits(values.inn, 12) &&
  isValidOptionalDigits(values.kpp, 9) &&
  isValidRequiredDigits(values.ogrn, 15) &&
  isValidOptionalText(values.creationDate, 10) &&
  isValidOptionalText(values.bank, 199) &&
  isValidRequiredText(values.address, undefined, 199) &&
  isValidRequiredDigits(values.telephone, 11) &&
  isValidRequiredEmail(values.email, 99) &&
  values.agreement === true;

const isOrgType1Valid = (values: FormValues) =>
  isValidRequiredText(values.name, 3, 99) &&
  isValidRequiredDigits(values.snils, 11) &&
  isValidRequiredDigits(values.passportNum, 10) &&
  isValidRequiredText(values.passportOrg, undefined, 199) &&
  isValidRequiredDigits(values.inn, 12) &&
  isValidRequiredText(values.address, undefined, 199) &&
  isValidRequiredDigits(values.telephone, 11) &&
  isValidRequiredEmail(values.email, 99) &&
  values.agreement === true;

type PartnerFormProps = {
  onSaved?: () => void;
};

export const PartnerForm = ({ onSaved }: PartnerFormProps) => {
  const {token} = useAuth();
  const {showError, showMessage} = useMessage();
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [isLoadingPartner, setIsLoadingPartner] = useState(false);

  const extractErrorMessage = (error: unknown) => {
    if (!error || typeof error !== 'object') {
      return undefined;
    }
    const maybeMessage = (error as { message?: string }).message;
    if (maybeMessage) {
      return maybeMessage;
    }
    const nestedMessage = (error as { error?: { message?: string } }).error?.message;
    if (nestedMessage) {
      return nestedMessage;
    }
    return (error as { data?: { message?: string } }).data?.message;
  };

  const mapPartnerToFormValues = (partner: PartnerEntity): FormValues => ({
    orgType: partner.orgType === 1 ? 1 : 0,
    name: partner.name ?? '',
    shortName: partner.shortName ?? '',
    director: partner.director ?? '',
    creationDate: partner.creationDate ?? '',
    snils: partner.snils ?? '',
    passportNum: partner.passportNum ?? '',
    passportOrg: partner.passportOrg ?? '',
    passportDate: partner.passportDate ?? '',
    kpp: partner.kpp ?? '',
    bank: partner.bank ?? '',
    inn: partner.inn ?? '',
    ogrn: partner.ogrn ?? '',
    address: partner.address ?? '',
    telephone: partner.telephone ?? '',
    email: partner.email ?? '',
    agreement: partner.agreement ?? false,
  });

  const form = useForm({
    defaultValues: {
      orgType: 0,
      name: '',
      shortName: '',
      director: '',
      creationDate: '',
      snils: '',
      passportNum: '',
      passportOrg: '',
      passportDate: '',
      kpp: '',
      bank: '',
      inn: '',
      ogrn: '',
      address: '',
      telephone: '',
      email: '',
      agreement: false,
    } as FormValues,
    onSubmit: async ({ value }) => {
      try {
        await savePartnerMutation.mutateAsync(value);
      } catch {
        // Errors are handled in onError
      }
    },
  });
  const Subscribe = form.Subscribe;
  type FieldMetaMap = typeof form.state.fieldMeta;

  const savePartnerMutation = useMutation<PartnerEntity, Error, FormValues>({
    mutationFn: async (payload: FormValues) => {
      if (!token) {
        throw new Error('Нет авторизации. Пожалуйста, войдите в аккаунт.');
      }
      const headers = {
        Authorization: `Bearer ${token}`,
      };
      if (partnerId) {
        const response = await fetch.PATCH('/partners/{id}', {
          params: {
            path: {
              id: partnerId,
            },
          },
          body: payload as unknown as UpdatePartnerDto,
          headers,
        });
        const error = (response as { error?: unknown }).error;
        if (error) {
          const message = extractErrorMessage(error);
          throw new Error(message ?? 'Ошибка при сохранении данных партнёра');
        }
        if (!response.data) {
          throw new Error('Ошибка при сохранении данных партнёра. Попробуйте позже.');
        }
        return response.data;
      }
      const response = await fetch.POST('/partners', {
        body: payload,
        headers,
      });
      const error = (response as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        throw new Error(message ?? 'Ошибка при сохранении данных партнёра');
      }
      if (!response.data) {
        throw new Error('Ошибка при сохранении данных партнёра. Попробуйте позже.');
      }
      return response.data;
    },
    onSuccess: (data) => {
      setPartnerId(data.id);
      const payload = mapPartnerToFormValues(data);
      (Object.keys(payload) as Array<keyof FormValues>).forEach((key) => {
        form.setFieldValue(key, payload[key]);
      });
      showMessage({
        message: 'Данные партнёра сохранены',
      });
      onSaved?.();
    },
    onError: (error) => {
      showError({
        message: error.message || 'Ошибка при сохранении данных партнёра.',
      });
    },
  });

  useEffect(() => {
    let isMounted = true;
    const loadPartner = async () => {
      if (!token) {
        return;
      }
      setIsLoadingPartner(true);
      const response = await fetch.GET('/partners', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!isMounted) {
        return;
      }
      const error = (response as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        showError({
          message: message ?? 'Ошибка загрузки данных партнёра',
        });
        setIsLoadingPartner(false);
        return;
      }
      const partners = response.data ?? [];
      const partner = partners[0];
      if (partner) {
        setPartnerId(partner.id);
        const payload = mapPartnerToFormValues(partner);
        (Object.keys(payload) as Array<keyof FormValues>).forEach((key) => {
          form.setFieldValue(key, payload[key]);
        });
      }
      setIsLoadingPartner(false);
    };
    loadPartner();
    return () => {
      isMounted = false;
    };
  }, [form, showError, token]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <div className="relative">
        {isLoadingPartner ? (
          <div className="mb-2">
            <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
          </div>
        ) : null}
        <fieldset className="p-0 m-0 border-none" disabled={isLoadingPartner}>
          <div className="flex flex-column gap-2">
        <Subscribe<FieldMetaMap> selector={(state) => state.fieldMeta}>
          {(fieldMeta) => {
            if (!fieldMeta) {
              return null;
            }
            const errors = Object.entries(fieldMeta).flatMap(([fieldName, meta]) => {
              const fieldErrors = (meta?.errors ?? []) as Array<string>;
              return fieldErrors.map((error) => ({
                field: fieldName,
                error,
              }));
            });
            if (errors.length) {
              return null;
            }
            return null;
          }}
        </Subscribe>
        <Field
          form={form}
          name="orgType"
        >
          {(field) => (
            <div className="flex flex-column gap-2">
              <label htmlFor={field.name}>Тип заявителя</label>
              <SelectButton
                id={field.name}
                value={field.state.value}
                options={ORG_TYPE_OPTIONS}
                allowEmpty={false}
                onChange={(e) => field.handleChange(e.value)}
              />
            </div>
          )}
        </Field>
        <Subscribe<number> selector={(state) => state.values.orgType}>
          {(orgType) => (
            <React.Fragment>
              {orgType === 0 ? (
                <React.Fragment>
                  <Field
                    form={form}
                    name="name"
                    validators={{
                      onChange: ({ value }) => required(value) ?? minLength(value, 3) ?? maxLength(value, 99),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>
                          <RequiredLabel>Наименование (ФИО для ИП)</RequiredLabel>
                        </label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={99}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field
                    form={form}
                    name="shortName"
                    validators={{
                      onChange: ({ value }) => maxLength(value, 30),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>Сокращенное наименование</label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={30}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field
                    form={form}
                    name="director"
                    validators={{
                      onChange: ({ value }) => required(value) ?? maxLength(value, 99),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>
                          <RequiredLabel>ФИО представителя</RequiredLabel>
                        </label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={99}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>

                  <div className="flex flex-column md:flex-row gap-2">
                    <div className="flex-1">
                      <Field
                        form={form}
                        name="inn"
                        validators={{
                          onChange: ({ value }) => required(value) ?? digitsOnly(value) ?? maxDigits(value, 12),
                        }}
                      >
                        {(field) => (
                          <div className="flex flex-column gap-2">
                            <label htmlFor={field.name}>
                              <RequiredLabel>ИНН</RequiredLabel>
                            </label>
                            <InputText
                              id={field.name}
                              name={field.name}
                              value={field.state.value}
                              maxLength={12}
                              className="w-full"
                              onBlur={field.handleBlur}
                              onChange={(e) => field.handleChange(e.target.value)}
                            />
                            {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                              <small className="p-error">{field.state.meta.errors[0]}</small>
                            ) : null}
                          </div>
                        )}
                      </Field>
                    </div>
                    <div className="flex-1">
                      <Field
                        form={form}
                        name="kpp"
                        validators={{
                      onChange: ({ value }) => digitsOnly(value) ?? maxDigits(value, 9),
                        }}
                      >
                        {(field) => (
                          <div className="flex flex-column gap-2">
                            <label htmlFor={field.name}>КПП</label>
                            <InputText
                              id={field.name}
                              name={field.name}
                              value={field.state.value}
                              maxLength={9}
                              className="w-full"
                              onBlur={field.handleBlur}
                              onChange={(e) => field.handleChange(e.target.value)}
                            />
                            {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                              <small className="p-error">{field.state.meta.errors[0]}</small>
                            ) : null}
                          </div>
                        )}
                      </Field>
                    </div>
                  </div>

                  <div className="flex flex-column md:flex-row gap-2">
                    <div className="flex-1">
                      <Field
                        form={form}
                        name="ogrn"
                        validators={{
                          onChange: ({ value }) => required(value) ?? digitsOnly(value) ?? maxDigits(value, 15),
                        }}
                      >
                        {(field) => (
                          <div className="flex flex-column gap-2">
                            <label htmlFor={field.name}>
                              <RequiredLabel>ОГРН/ОГРНИП</RequiredLabel>
                            </label>
                            <InputText
                              id={field.name}
                              name={field.name}
                              value={field.state.value}
                              maxLength={15}
                              className="w-full"
                              onBlur={field.handleBlur}
                              onChange={(e) => field.handleChange(e.target.value)}
                            />
                            {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                              <small className="p-error">{field.state.meta.errors[0]}</small>
                            ) : null}
                          </div>
                        )}
                      </Field>
                    </div>
                    <div className="flex-1">
                      <Field
                        form={form}
                        name="creationDate"
                        validators={{
                      onChange: ({ value }) => maxLength(value, 10),
                        }}
                      >
                        {(field) => (
                          <div className="flex flex-column gap-2">
                            <label htmlFor={field.name}>Дата присвоения ОГРН</label>
                            <DatePicker
                              id={field.name}
                              value={field.state.value}
                              className="w-full"
                              onBlur={field.handleBlur}
                              onChange={field.handleChange}
                            />
                            {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                              <small className="p-error">{field.state.meta.errors[0]}</small>
                            ) : null}
                          </div>
                        )}
                      </Field>
                    </div>
                  </div>
                  <Field
                    form={form}
                    name="bank"
                    validators={{
                      onChange: ({ value }) => maxLength(value, 199),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>Банковские реквизиты</label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={199}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field
                    form={form}
                    name="address"
                    validators={{
                      onChange: ({ value }) => required(value) ?? maxLength(value, 199),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>
                          <RequiredLabel>Адрес</RequiredLabel>
                        </label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={199}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field
                    form={form}
                    name="telephone"
                    validators={{
                      onChange: ({ value }) => required(value) ?? digitsOnly(value) ?? maxDigits(value, 11),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>
                          <RequiredLabel>Телефон</RequiredLabel>
                        </label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={11}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field
                    form={form}
                    name="email"
                    validators={{
                      onChange: ({ value }) => required(value) ?? emailFormat(value) ?? maxLength(value, 99),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>
                          <RequiredLabel>Email</RequiredLabel>
                        </label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={99}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Field
                    form={form}
                    name="name"
                    validators={{
                      onChange: ({ value }) => required(value) ?? minLength(value, 3) ?? maxLength(value, 99),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>
                          <RequiredLabel>ФИО</RequiredLabel>
                        </label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={99}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field
                    form={form}
                    name="snils"
                    validators={{
                      onChange: ({ value }) => required(value) ?? digitsOnly(value) ?? maxDigits(value, 11),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>
                          <RequiredLabel>СНИЛС</RequiredLabel>
                        </label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={11}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field
                    form={form}
                    name="passportNum"
                    validators={{
                      onChange: ({ value }) => required(value) ?? digitsOnly(value) ?? maxDigits(value, 10),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>
                          <RequiredLabel>Паспорт серия и №</RequiredLabel>
                        </label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={10}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field
                    form={form}
                    name="passportOrg"
                    validators={{
                      onChange: ({ value }) => required(value) ?? maxLength(value, 199),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>
                          <RequiredLabel>Кем выдан</RequiredLabel>
                        </label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={199}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field
                    form={form}
                    name="inn"
                    validators={{
                      onChange: ({ value }) => required(value) ?? digitsOnly(value) ?? maxDigits(value, 12),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>
                          <RequiredLabel>ИНН</RequiredLabel>
                        </label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={12}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field
                    form={form}
                    name="address"
                    validators={{
                      onChange: ({ value }) => required(value) ?? maxLength(value, 199),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>
                          <RequiredLabel>Адрес</RequiredLabel>
                        </label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={199}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field
                    form={form}
                    name="telephone"
                    validators={{
                      onChange: ({ value }) => required(value) ?? digitsOnly(value) ?? maxDigits(value, 11),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>
                          <RequiredLabel>Телефон</RequiredLabel>
                        </label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={11}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                  <Field
                    form={form}
                    name="email"
                    validators={{
                      onChange: ({ value }) => required(value) ?? emailFormat(value) ?? maxLength(value, 99),
                    }}
                  >
                    {(field) => (
                      <div className="flex flex-column gap-2">
                        <label htmlFor={field.name}>
                          <RequiredLabel>Email</RequiredLabel>
                        </label>
                        <InputText
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          maxLength={99}
                          className="w-full"
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.isTouched && field.state.meta.errors[0] ? (
                          <small className="p-error">{field.state.meta.errors[0]}</small>
                        ) : null}
                      </div>
                    )}
                  </Field>
                </React.Fragment>
              )}
            </React.Fragment>
          )}
        </Subscribe>
        <Field
          form={form}
          name="agreement"
        >
          {(field) => (
            <div className="flex align-items-center gap-2">
              <Checkbox
                inputId={field.name}
                checked={field.state.value ?? false}
                onChange={(e) => field.handleChange(e.checked ?? false)}
              />
              <label htmlFor={field.name}>
                <span>
                  Настоящим выражаю согласие на передачу и использование персональных данных. Понимаю, что персональные
                  данные могут быть получены и использованы не только Сетевой компанией, но и Гарантирующим поставщиком.
                  <RequiredMark />
                </span>
              </label>
            </div>
          )}
        </Field>

        <div className="mt-2" />
            <Subscribe<[FormValues, boolean]>
            selector={(state) => [state.values as FormValues, state.isSubmitting]}
          >
            {([values, isSubmitting]) => {
              const isSubmitEnabled =
                values.orgType === 0 ? isOrgType0Valid(values) : values.orgType === 1 ? isOrgType1Valid(values) : false;
              return (
                <div className="flex justify-content-start">
                  <Button
                    type="submit"
                    disabled={!isSubmitEnabled || isSubmitting || isLoadingPartner}
                    loading={isSubmitting || isLoadingPartner}
                    className="w-auto"
                  >
                    Сохранить данные заявителя
                  </Button>
                </div>
              );
            }}
            </Subscribe>
          </div>
        </fieldset>
        {isLoadingPartner ? (
          <div
            className="absolute top-0 left-0 right-0 bottom-0 flex align-items-center justify-content-center"
            style={{ backdropFilter: 'blur(4px)', backgroundColor: 'rgba(255, 255, 255, 0.4)' }}
          />
        ) : null}
      </div>
    </form>
  );
}